"use client"; // Ensure it's client-rendered

import React, { useEffect, useState } from "react";
import Link from "next/link";
import classNames from "classnames";
import { getAuctionLink } from "@/lib/links";
import { Auction } from "@/types/auction";

interface AuctionBottomNavbarProps {
  allAuctions: Auction[];
  currentPartId: number;
  onNavigate?: (newSortNo: number) => void;
  isLastPage?: boolean;
}

const AuctionBottomNavbar: React.FC<AuctionBottomNavbarProps> = ({
  allAuctions,
  currentPartId,
  isLastPage = false,
}) => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted || !allAuctions) return null;

  const currentIndex = allAuctions.findIndex(
    (auction) => auction.partId === currentPartId
  );

  const hasPrevious = currentIndex >= 0 && currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allAuctions.length - 1;

  const previousItem = hasPrevious
    ? allAuctions[currentIndex - 1]
    : (null as any);
  const nextItem = hasNext ? allAuctions[currentIndex + 1] : (null as any);

  const IconArrow = ({ direction }: { direction: "left" | "right" }) => (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={classNames("w-4 h-4 hidden sm:block", {
        "rotate-180": direction === "right",
      })}
    >
      <path
        d="M15.7051 7.70443L11.1251 12.2944L15.7051 16.8844L14.2951 18.2944L8.29512 12.2944L14.2951 6.29443L15.7051 7.70443Z"
        fill="currentColor"
      />
    </svg>
  );

  const formatTitle = (title: string) => {
    if (title.length > 40) {
      return title.substring(0, 40) + "...";
    }

    return title;
  };

  if ((!hasPrevious && !hasNext) || !isLastPage) return null;

  return (
    <div className="bg-aliceblue-dark py-4 md:p-4 my-4 rounded-lg">
      <div className="container flex flex-col md:flex-row items-center md:justify-between w-full !space-x-4">
        {hasPrevious && previousItem ? (
          <span className="flex flex-col sm:flex-row items-center space-x-2 mb-6 md:mb-0">
            <Link
              href={getAuctionLink(previousItem)}
              className="flex flex-col md:flex-row items-center space-x-2 text-black hover:bg-blue-600 rounded !no-underline"
            >
              <IconArrow direction="left" />
              <span className="sm:block font-bold text-xs md:text-base">
                {formatTitle(previousItem.auctionTitle)}
              </span>

              <span className="text-paragraph/80 font-bold md:font-normal text-xs md:text-base !no-underline">
                <span className="inline sm:hidden mr-2">←</span> Föregående
                auktion
              </span>
            </Link>
          </span>
        ) : (
          <div />
        )}

        {hasNext && nextItem ? (
          <span className="flex flex-col sm:flex-row items-center space-x-2">
            <Link
              href={getAuctionLink(nextItem)}
              className="flex flex-col-reverse md:flex-row items-center space-x-2 text-black hover:bg-blue-600 rounded !no-underline"
            >
              <span className="text-paragraph/80 font-bold md:font-normal text-xs md:text-base !no-underline">
                Nästa auktion <span className="inline sm:hidden ml-2">→</span>
              </span>
              <span className="sm:block font-bold text-xs md:text-base">
                {formatTitle(nextItem.auctionTitle)}
              </span>
              <IconArrow direction="right" />
            </Link>
          </span>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};

export default AuctionBottomNavbar;
